import { PAGE_API_QUERY_KEY_HEAD } from '@Core/clients/pageApiQueryKeys';
import { createApi } from '@Pages/pageRuntime/sdk/api';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkPaymentRefundPreviewHandle,
  SdkPaymentRefundPreviewOptions
} from '@Pages/pageRuntime/sdk/types';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export function usePaymentRefundPreview({
  resourceId,
  recordId,
  enabled = true
}: SdkPaymentRefundPreviewOptions): SdkPaymentRefundPreviewHandle {
  const canPreview = enabled && resourceId.length > 0 && recordId.length > 0;
  const query = useQuery<
    Diagonal.Core.Data.PaymentRefundPreviewData,
    ReturnType<typeof toApiError>
  >({
    queryKey: [PAGE_API_QUERY_KEY_HEAD, 'records', resourceId, recordId, 'refund-preview'],
    enabled: canPreview,
    retry: false,
    refetchOnMount: 'always',
    staleTime: 0,
    throwOnError: false,
    queryFn: async ({ signal }) => {
      return createApi(axios).get<Diagonal.Core.Data.PaymentRefundPreviewData>(
        `/api/data/resources/${encodeURIComponent(resourceId)}/records/${encodeURIComponent(recordId)}/refund-preview`,
        { signal }
      );
    }
  });
  const isLoading = canPreview && (query.isFetching || query.isPaused);
  const preview = canPreview && !isLoading && !query.isError ? query.data : undefined;

  return {
    preview,
    isLoading,
    error: canPreview ? query.error : null,
    refetch: async () => {
      if (canPreview) {
        await query.refetch();
      }
    }
  };
}
